import React, { useState, useEffect } from 'react';
import { SideBar } from '../../layout';
import * as mui from '@mui/material';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import ResponsiveContainer from '../../layout/Container';
import axios from 'axios'; // Import Axios
import { useNavigate } from 'react-router-dom';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CustomSpeedDial from '../../common/SpeedDial';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Modal from '../../common/Modal';
import NormalTextField from '../../common/NormalTextField';
import permissionService from '../../../services/PermissionService';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DynamicAvatarList from '../../common/AvatarName';
import UserCard from '../../common/UserCard';
import userService from '../../../services/UserService';
import LoginIcon from '@mui/icons-material/Login';
import roleService from '../../../services/RoleService';
import MultipleSelect from '../../common/MultipleSelect';
import companyService from '../../../services/CompanyService';
import userrolesService from '../../../services/UserRoleService';
import DynamicSnackbar from '../../common/Snackbar';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { UsersIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BadgeIcon from '@mui/icons-material/Badge';
import Cookies from 'js-cookie';
import { formatDistanceToNow, format } from 'date-fns';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AuthService from '../../../services/AuthService';


const ManageUsers = () => {

    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [users, setUsers] = useState([]);
    const [userRoles, setUserRoles] = useState({}); // Store user roles by username
    const [roles, setRoles] = useState([]);
    const [roleList, setRoleList] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [displayMode, setDisplayMode] = useState('list'); // 'grid' or 'list'
    const [searchQuery, setSearchQuery] = useState('');
    const [emailExists, setEmailExists] = useState(false);
    const [usernameExists, setUsernameExists] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false); // Add state for edit mode
    const [currentEditingUser, setCurrentEditingUser] = useState(null); // Track current user being edited

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const navigate = useNavigate();

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const [selectedCompany, setSelectedCompany] = useState([]);

    const [userData, setNewUsers] = useState({
        first_name: '',
        last_name: '',
        email: '',
        is_active: true,
        groups: '',
        deactivation_reason: '' // Add this new field
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNewUsers({ ...userData, [name]: value });
        
        // Check for duplicate email while typing
        if (name === 'email' && value) {
            // Skip duplicate check if in edit mode and email hasn't changed
            if (isEditMode && currentEditingUser && 
                currentEditingUser.email && 
                value.toLowerCase() === currentEditingUser.email.toLowerCase()) {
                console.log("Email unchanged in edit mode, skipping duplicate validation");
                setEmailExists(false);
                setUsernameExists(false);
                return;
            }

            const isDuplicate = users.some(user => 
                user.email && 
                user.email.toLowerCase() === value.toLowerCase() &&
                (!isEditMode || (isEditMode && user.username !== currentEditingUser.username))
            );
            setEmailExists(isDuplicate);
            
            // Also check if this email is already used as a username
            const isUsernameExists = users.some(user => 
                user.username && 
                user.username.toLowerCase() === value.toLowerCase() &&
                (!isEditMode || (isEditMode && user.username !== currentEditingUser.username))
            );
            setUsernameExists(isUsernameExists);
        } else if (name === 'email') {
            setEmailExists(false);
            setUsernameExists(false);
        }
    };

    const handleCompanyChange = (event) => {
        // Extract the selected values from the event
        const selectedValues = event.target.value;
        // Convert to array format with value/label pairs to maintain compatibility
        const formattedValues = Array.isArray(selectedValues) ? 
            selectedValues.map(value => {
                const company = companyList.find(c => c.value === value);
                return company || { value, label: `Company ${value}` };
            }) : [];
        
        setSelectedCompany(formattedValues);
    };

    const handleRoleChange = (event) => {
        // Extract the selected values from the event
        const selectedValues = event.target.value;
        // Convert to array format with value/label pairs to maintain compatibility
        const formattedValues = Array.isArray(selectedValues) ? 
            selectedValues.map(value => {
                const role = roleList.find(r => r.value === value);
                return role || { value, label: `Role ${value}` };
            }) : [];
        
        setSelectedRoles(formattedValues);
    };

    const handleOpenCreateModal = () => {
        // Reset form data
        setNewUsers({
            first_name: '',
            last_name: '',
            email: '',
            is_active: true,
            groups: '',
            deactivation_reason: '' // Add this new field
        });
        
        // Reset edit mode
        setIsEditMode(false);
        setCurrentEditingUser(null);
        
        // Pre-select the first role by default if roleList is available
        if (roleList && roleList.length > 0) {
            // Select the first role as default
            const defaultRole = roleList[0];
            console.log("Pre-selecting default role:", defaultRole);
            setSelectedRoles([defaultRole]);
        } else {
            // Fallback to hardcoded default role if no roleList available
            const defaultRole = { value: 1, label: 'Administrator' };
            console.log("Pre-selecting hardcoded default role:", defaultRole);
            setSelectedRoles([defaultRole]);
        }
        
        // Pre-select a company if available
        if (companyList && companyList.length > 0) {
            const defaultCompany = companyList[0];
            console.log("Pre-selecting default company:", defaultCompany);
            setSelectedCompany([defaultCompany]);
        }
        
        setFormSubmitted(false);
        setEmailExists(false);
        setUsernameExists(false);
        
        setOpenCreateModal(true);
    };

    const handleOpenEditModal = (user) => {
        console.log("Opening edit modal for user:", user);
        
        // Validate we have a user with minimum required fields
        if (!user || ((!user.username && !user.email) && !user.id)) {
            console.error("Invalid user data for editing:", user);
            setSnackbarMessage('Error: Invalid user data');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        // Set edit mode
        setIsEditMode(true);
        setCurrentEditingUser(user);
        
        // Ensure user data has all needed fields, especially username
        // If username is missing but email exists, use email as username
        if (!user.username && user.email) {
            console.log("User missing username, using email as username for edit:", user.email);
            user = {
                ...user,
                username: user.email
            };
            setCurrentEditingUser(user);
        }
        
        // Set user data in the form
        setNewUsers({
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            is_active: user.is_active !== undefined ? user.is_active : true,
            // Use email if username is missing
            username: user.username || user.email || ''
        });
        
        // Log the roles object to help debug
        console.log("Looking for roles for user identifier:", user.username || user.email);
        
        // Get user roles if available
        if (userRoles[user.username]) {
            const userRoleInfo = userRoles[user.username];
            console.log("Found role info for user:", userRoleInfo);
            const selectedRolesList = [];
            
            // Add roles from Django groups
            if (userRoleInfo.djangoGroups && userRoleInfo.djangoGroups.length > 0) {
                console.log("User has Django groups:", userRoleInfo.djangoGroups);
                userRoleInfo.djangoGroups.forEach(groupName => {
                    // Find role in roleList by name
                    const matchingRole = roleList.find(role => 
                        role.label === groupName || 
                        role.label.toLowerCase() === groupName.toLowerCase()
                    );
                    
                    if (matchingRole) {
                        console.log("Matched role:", matchingRole);
                        selectedRolesList.push(matchingRole);
                    } else {
                        console.log("Could not match role for group:", groupName);
                    }
                });
            } else {
                console.log("User has no Django groups or groups array is empty");
            }
            
            // Set selected roles if found, otherwise use default
            if (selectedRolesList.length > 0) {
                console.log("Setting selected roles:", selectedRolesList);
                setSelectedRoles(selectedRolesList);
            } else if (roleList && roleList.length > 0) {
                console.log("No roles found for user, using default role");
                setSelectedRoles([roleList[0]]);
            }
        } else {
            console.log("No role info found for username:", user.username);
            if (roleList && roleList.length > 0) {
                // Default to first role if no roles are found
                console.log("Using default role");
                setSelectedRoles([roleList[0]]);
            }
        }
        
        // For companies, we'd ideally fetch the user's companies
        // For now, default to first company (or implement fetching logic)
        if (companyList && companyList.length > 0) {
            console.log("Setting default company");
            setSelectedCompany([companyList[0]]);
        }
        
        setFormSubmitted(false);
        setEmailExists(false);
        setUsernameExists(false);
        
        setOpenCreateModal(true);
    };

    const handleCloseCreateModal = (event, reason) => {
        if (reason == 'backdropClick') {
            setOpenCreateModal(true)
        }
        else {
            setOpenCreateModal(false);
            // Reset edit mode when closing
            setIsEditMode(false);
            setCurrentEditingUser(null);
        }
    };

    const createOrUpdateUser = async (e) => {
        e.preventDefault();
        try {
            setFormSubmitted(true);

            // Check for required fields - now email is both email and username
            if (!userData.first_name || !userData.last_name || !userData.email) {
                setSnackbarMessage('Please fill in all required fields');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }

            // Only check for duplicate email/username if we're creating a new user
            // or if the email has changed in edit mode
            if (!isEditMode || (isEditMode && currentEditingUser && userData.email !== currentEditingUser.email)) {
                // Check if this email is already used as a username
                const isDuplicateUsername = users.some(user => 
                    user.username && user.username.toLowerCase() === userData.email.toLowerCase() &&
                    (!isEditMode || (isEditMode && user.username !== currentEditingUser.username))
                );

                if (isDuplicateUsername) {
                    setUsernameExists(true);
                    setSnackbarMessage('A user with this email (as username) already exists');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }
                
                // Check for duplicate email
                const isDuplicateEmail = users.some(user => 
                    user.email && user.email.toLowerCase() === userData.email.toLowerCase() &&
                    (!isEditMode || (isEditMode && user.username !== currentEditingUser.username))
                );

                if (isDuplicateEmail) {
                    setEmailExists(true);
                    setSnackbarMessage('A user with this email already exists');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }
            }

            //CREATE OR UPDATE USER with email as username
            const userDataWithRoles = {
                ...userData,
                username: userData.email, // Use email as username
                groups: selectedRoles.map(role => {
                    // Ensure the role ID is a number, not a string or null
                    let roleId = role.value;
                    
                    // Convert string to number if needed
                    if (typeof roleId === 'string' && !isNaN(parseInt(roleId, 10))) {
                        roleId = parseInt(roleId, 10);
                    }
                    
                    return roleId;
                }).filter(id => id !== null && !isNaN(id)) // Filter out any null or NaN values
            };

            console.log(`${isEditMode ? "Updating" : "Creating"} user data with groups:`, userDataWithRoles);

            let userResponse;
            if (isEditMode && currentEditingUser) {
                // Update existing user
                console.log("Updating user with ID:", currentEditingUser.id);
                
                // Create update data that preserves the original identifier
                const updateData = {...userDataWithRoles};
                
                // If the email hasn't changed, don't include username in update
                if (currentEditingUser.email === updateData.email) {
                    console.log("Email unchanged, removing username from update data");
                    delete updateData.username;
                } else {
                    console.log("Email changing from", currentEditingUser.email, "to", updateData.email);
                    console.log("Username will update to:", updateData.username);
                }
                
                try {
                    // Use the user ID as the identifier - much more reliable than username/email
                    if (!currentEditingUser.id) {
                        throw new Error("User ID is missing, cannot update");
                    }
                    
                    console.log("Sending update request using user ID:", currentEditingUser.id);
                    console.log("With data:", updateData);
                    
                    // Use our new specific endpoint for updating by ID
                    userResponse = await userService.updateUserByNumericId(currentEditingUser.id, updateData);
                    console.log("Update successful:", userResponse);
                } catch (updateError) {
                    console.error("Error during user update:", updateError);
                    console.error("Response data:", updateError.response?.data);
                    
                    // Handle error
                    let errorMessage = `Error updating user: ${updateError.message}`;
                    if (updateError.response?.data?.detail) {
                        errorMessage = `Update error: ${updateError.response.data.detail}`;
                    }
                    
                    setSnackbarMessage(errorMessage);
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }
            } else {
                // Create new user
                userResponse = await userService.createUser(userDataWithRoles);
            }

            // Extract company UUIDs from the selected companies
            // IMPORTANT: Don't convert UUIDs to numbers - keep them as strings
            const companyIdArray = selectedCompany.map(company => {
                // Make sure we're using the UUID as-is, not trying to convert to number
                const companyId = company.value;
                console.log("Using company UUID:", companyId);
                return companyId;
            }).filter(id => id !== null); // Just filter out null values

            if (userResponse) {
                // For edit mode, use the currentEditingUser id, otherwise use the response id
                const userId = isEditMode ? currentEditingUser.id || currentEditingUser.username : userResponse.id;
                
                const userroleData = {
                    COMPANY_ID: companyIdArray,
                    USERNAME: userId
                };

                console.log("Submitting user role data:", userroleData);
                const userRolesResponse = await userrolesService.create(userroleData);
            }

            handleCloseCreateModal();

            setSnackbarMessage(`User successfully ${isEditMode ? 'updated' : 'created'}`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            // Update users list based on operation
            if (isEditMode && currentEditingUser) {
                setUsers(users.map(user => 
                    user.username === currentEditingUser.username ? {...user, ...userDataWithRoles} : user
                ));
            } else {
                setUsers([...users, userResponse]);
            }

            // Add a delay before refreshing the page
            setTimeout(() => {
                // Refresh the page
                window.location.reload();
            }, 2000); // 2-second delay

        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} user:`, error);
            console.error("Error response:", error.response?.data);
            setSnackbarMessage(`There was a problem ${isEditMode ? 'updating' : 'creating'} a user: ` + (error.response?.data?.detail || error.message));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    axios.defaults.xsrfCookieName = 'csrftoken';
    axios.defaults.xsrfHeaderName = 'X-CSRFToken';

    const fetchUsers = async () => {
        try {
            console.log("Fetching users...");
            
            // Add a delay to ensure API is ready
            await new Promise(resolve => setTimeout(resolve, 500));
            
            const response = await userService.fetchUsers();
            
            console.log("Users API response received");
            
            if (!response) {
                console.error("Empty response from users API");
                setSnackbarMessage('Error: No data received from users API');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return [];
            }
            
            // Validate and process the response
            if (Array.isArray(response)) {
                console.log(`Fetched ${response.length} users`);
                
                // Validate each user has username field (critical for the update API)
                const validatedUsers = response.map(user => {
                    // If username is missing but email exists, use email as username
                    if (!user.username && user.email) {
                        console.log(`User without username, using email as username:`, user.email);
                        return { ...user, username: user.email };
                    }
                    return user;
                });
                
                // Log sample data for debugging
                if (validatedUsers.length > 0) {
                    console.log("Sample user data:", validatedUsers[0]);
                }
                
                // Update state with validated users
                setUsers(validatedUsers);
                return validatedUsers;
            } else {
                console.error("API response is not an array:", typeof response);
                setSnackbarMessage('Error: Invalid response format from users API');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return [];
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            console.error('Error details:', error.response?.data || error.message);
            
            // Check for token validation error
            const errorDetail = error.response?.data?.detail || '';
            if (typeof errorDetail === 'string' && errorDetail.includes('Given token not valid for any token type')) {
                console.error('Token validation error detected. Forcing logout...');
                setSnackbarMessage('Your session has expired. Please log in again.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                
                // Short delay to show the message before redirect
                setTimeout(() => {
                    AuthService.logout();
                }, 1500);
                
                return [];
            }
            
            // Handle other errors
            setSnackbarMessage('Error loading users: ' + (error.response?.data?.detail || error.message));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return [];
        }
    };

    // Load users when component mounts
    useEffect(() => {
        console.log("Component mounted, fetching users");
        fetchUsers().then(fetchedUsers => {
            if (fetchedUsers.length > 0) {
                console.log(`Loaded ${fetchedUsers.length} users from API`);
            } else {
                console.warn("No users loaded or empty response");
            }
        });
    }, []);

    // New function to fetch user roles separately
    const fetchUserRoles = async () => {
        try {
            // Fetch roles for all users
            const auditUserResponse = await roleService.fetchUsers();
            
            // Fetch Django groups
            const groupsResponse = await roleService.fetchDjangoGroups();
            
            // Create a combined role mapping object by username
            const rolesMap = {};
            
            // Add audit user roles
            if (auditUserResponse && Array.isArray(auditUserResponse)) {
                auditUserResponse.forEach(auditUser => {
                    if (auditUser.user && auditUser.user.username && auditUser.role) {
                        // Initialize the user entry if it doesn't exist
                        if (!rolesMap[auditUser.user.username]) {
                            rolesMap[auditUser.user.username] = {
                                auditRole: null,
                                djangoGroups: [],
                                isSuperuser: false
                            };
                        }
                        
                        // Add the audit role - handle different response formats
                        let roleName = null;
                        if (typeof auditUser.role === 'string') {
                            roleName = auditUser.role;
                        } else if (auditUser.role.name) {
                            roleName = auditUser.role.name;
                        } else if (auditUser.role.role_name) {
                            roleName = auditUser.role.role_name;
                        } else if (auditUser.role.ROLE_NAME) {
                            roleName = auditUser.role.ROLE_NAME;
                        }
                        
                        rolesMap[auditUser.user.username].auditRole = roleName;
                    }
                });
            }
            
            // Process and map Django user groups
            if (users && Array.isArray(users)) {
                users.forEach(user => {
                    if (user.username) {
                        // Initialize user entry if it doesn't exist
                        if (!rolesMap[user.username]) {
                            rolesMap[user.username] = {
                                auditRole: null,
                                djangoGroups: [],
                                isSuperuser: false
                            };
                        }
                        
                        // Check if user is a superuser
                        if (user.is_superuser) {
                            rolesMap[user.username].isSuperuser = true;
                        }
                        
                        // Add Django groups if available
                        if (user.groups && Array.isArray(user.groups)) {
                            const groupNames = user.groups.map(groupId => {
                                // Try to find the group name from the fetched roles array
                                if (roles && Array.isArray(roles)) {
                                    const group = roles.find(r => r.id === groupId);
                                    if (group) {
                                        // Return the name property if it exists
                                        return group.name || group.NAME || `Group ${groupId}`;
                                    }
                                }
                                
                                // If we couldn't find a match in roles, check the groups response
                                if (groupsResponse && Array.isArray(groupsResponse)) {
                                    const group = groupsResponse.find(g => g.id === groupId);
                                    if (group) {
                                        return group.name || group.NAME || `Group ${groupId}`;
                                    }
                                }
                                
                                // If we still don't have a name, check if groupId is an object with a name property
                                if (typeof groupId === 'object' && groupId !== null) {
                                    return groupId.name || groupId.NAME || JSON.stringify(groupId);
                                }
                                
                                // Fallback to just using the ID
                                return `Role ${groupId}`;
                            })
                            .filter(name => name); // Filter out any undefined or empty names
                            
                            rolesMap[user.username].djangoGroups = groupNames;
                        }
                    }
                });
            }
            
            console.log("Combined user roles map:", rolesMap);
            setUserRoles(rolesMap);
        } catch (error) {
            console.error('Error fetching user roles:', error);
        }
    };
    
    // Helper function to render role badges
    const renderRoleBadges = (username) => {
        const userRoleInfo = userRoles[username];
        
        if (!userRoleInfo) {
            return null;
        }
        
        return (
            <mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {/* Show Superuser badge if the user is a superuser */}
                {userRoleInfo.isSuperuser && (
                    <mui.Chip
                        label="Superuser"
                        size="small"
                        icon={<BadgeIcon style={{ fontSize: '0.8rem' }} />}
                        sx={{
                            backgroundColor: 'rgba(220, 53, 69, 0.08)',
                            color: '#dc3545',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: '20px',
                            border: '1px solid',
                            borderColor: 'rgba(220, 53, 69, 0.2)'
                        }}
                    />
                )}
                
                {/* Show Audit Role badge if it exists and user is not a superuser */}
                {userRoleInfo.auditRole && (
                    <mui.Chip
                        label={userRoleInfo.auditRole}
                        size="small"
                        icon={<BadgeIcon style={{ fontSize: '0.8rem' }} />}
                        sx={{
                            backgroundColor: 'rgba(121, 82, 179, 0.08)',
                            color: '#7952b3',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: '20px',
                            border: '1px solid',
                            borderColor: 'rgba(121, 82, 179, 0.2)'
                        }}
                    />
                )}
                
                {/* Show Django Group badges - filter out 'Administrator' for superusers */}
                {userRoleInfo.djangoGroups && userRoleInfo.djangoGroups.length > 0 && 
                    userRoleInfo.djangoGroups
                        .filter(group => {
                            // Skip 'Administrator' group for superusers to avoid redundancy
                            if (userRoleInfo.isSuperuser && 
                                (group === 'Administrator' || group === 'Admin')) {
                                return false;
                            }
                            return true;
                        })
                        .map((group, index) => {
                            // Skip rendering if we have an object string or empty value
                            if (!group || group.includes('[object Object]') || group === 'undefined') {
                                return null;
                            }
                            
                            // Ensure we have a string
                            const groupName = typeof group === 'string' ? group : 
                                (typeof group === 'object' ? (group.name || JSON.stringify(group)) : String(group));
                                
                            return (
                                <mui.Chip
                                    key={index}
                                    label={groupName}
                                    size="small"
                                    sx={{
                                        backgroundColor: 'rgba(25, 135, 84, 0.08)',
                                        color: '#198754',
                                        fontWeight: 500,
                                        fontSize: '0.7rem',
                                        height: '20px',
                                        border: '1px solid',
                                        borderColor: 'rgba(25, 135, 84, 0.2)'
                                    }}
                                />
                            );
                        })
                }
            </mui.Box>
        );
    };

    useEffect(() => {
        if (users.length > 0 && roles.length > 0) {
            fetchUserRoles();
        }
    }, [users, roles]);

    const fetchRoles = async () => {
        try {
            const response = await roleService.fetchRoles();
            console.log("Raw roles response:", response);
            
            // Log the structure of the response to help diagnose the issue
            console.log("Roles response type:", typeof response);
            console.log("Is array:", Array.isArray(response));
            
            if (response) {
                // Handle different response formats
                let roleData = response;
                
                // Log each role for debugging
                if (Array.isArray(roleData)) {
                    console.log(`Got ${roleData.length} roles`);
                    roleData.forEach((role, index) => {
                        console.log(`Role ${index}:`, role);
                    });
                }
                
                setRoles(roleData);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    useEffect(() => {
        fetchRoles();
        console.log("Fetching roles data...");
        
        // Directly set roleList with hardcoded Django auth groups
        // This bypasses any API issues and ensures dropdown always has options
        const defaultRoleOptions = [
            { value: 1, label: 'Administrator' },
            { value: 2, label: 'Internal Auditor' },
            { value: 3, label: 'Process Owner' },
            { value: 4, label: 'Staff' },
            { value: 5, label: 'Manager' }
        ];
        
        console.log("Setting hardcoded role options for dropdown:", defaultRoleOptions);
        setRoleList(defaultRoleOptions);
        
        // Also update roles for consistency
        setTimeout(() => {
            if (roles.length === 0) {
                console.log("Setting hardcoded role data");
                const defaultDjangoGroups = [
                    { id: 1, name: 'Administrator' },
                    { id: 2, name: 'Internal Auditor' },
                    { id: 3, name: 'Process Owner' },
                    { id: 4, name: 'Staff' },
                    { id: 5, name: 'Manager' }
                ];
                setRoles(defaultDjangoGroups);
            }
            
            // Double check roleList to ensure there are no zero values
            setRoleList(prevRoleList => {
                // Filter out any options with value 0
                const filteredList = prevRoleList.filter(role => role.value !== 0);
                
                if (filteredList.length !== prevRoleList.length) {
                    console.log("Removed zero-value roles from roleList");
                }
                
                console.log("Final roleList:", filteredList);
                return filteredList;
            });
        }, 500);
    }, []);

    const [companies, setCompanies] = useState([]);
    const [companyList, setCompanyList] = useState([]);

    const fetchCompanies = async () => {
        try {
            console.log("Fetching companies from API...");
            const response = await companyService.fetchCompanies();
            console.log("Companies API response:", response);
            
            if (response && Array.isArray(response) && response.length > 0) {
                console.log("Company data structure example:", response[0]);
                setCompanies(response);
            } else {
                console.warn("Company API returned no data or invalid format");
                setCompanies([]);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            setCompanies([]);
        }
    };

    const handleButtonClick = (username) => {
        console.log("Attempting to edit user with identifier:", username);
        console.log("Available users count:", users.length);
        
        if (!username) {
            console.error("No identifier provided for editing");
            setSnackbarMessage('Error: Invalid user identifier');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        // First, try to find the user by ID (most reliable)
        let userToEdit = users.find(user => 
            user.id && user.id.toString() === username.toString()
        );
        
        if (userToEdit) {
            console.log("Found user by ID match:", userToEdit);
        } else {
            // If not found by ID, try other strategies
            
            // Strategy 1: Direct email match (since email is used as username)
            userToEdit = users.find(user => 
                user.email && user.email === username
            );
            
            if (userToEdit) {
                console.log("Found user by exact email match:", userToEdit);
            }
            
            // Strategy 2: Direct username match if no email match was found
            if (!userToEdit) {
                userToEdit = users.find(user => 
                    user.username && user.username === username
                );
                
                if (userToEdit) {
                    console.log("Found user by exact username match:", userToEdit);
                }
            }
            
            // Strategy 3: Case-insensitive email match
            if (!userToEdit) {
                userToEdit = users.find(user => 
                    user.email && 
                    typeof user.email === 'string' &&
                    user.email.toLowerCase() === username.toLowerCase()
                );
                
                if (userToEdit) {
                    console.log("Found user by case-insensitive email match:", userToEdit);
                }
            }
            
            // Strategy 4: Case-insensitive username match
            if (!userToEdit) {
                userToEdit = users.find(user => 
                    user.username && 
                    typeof user.username === 'string' &&
                    user.username.toLowerCase() === username.toLowerCase()
                );
                
                if (userToEdit) {
                    console.log("Found user by case-insensitive username match:", userToEdit);
                }
            }
        }
        
        if (userToEdit) {
            console.log("Final user to edit:", userToEdit);
            
            // Ensure we have an ID for API lookup
            if (!userToEdit.id) {
                console.error("Found user but missing ID required for update API");
                setSnackbarMessage('Error: User data missing required ID');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }
            
            // Open edit modal with the user data
            handleOpenEditModal(userToEdit);
        } else {
            console.error("User not found for editing:", username);
            console.error("No matching user found in the users list with", users.length, "users");
            
            // For debugging - log some sample data
            if (users.length > 0) {
                console.log("First few users:", users.slice(0, 3).map(u => ({
                    id: u.id,
                    username: u.username,
                    email: u.email
                })));
            }
            
            setSnackbarMessage('User not found. Please refresh the page and try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        if (companies && companies.length > 0) {
            console.log("Processing companies for dropdown:", companies);
            
            // Process company data for dropdown - now ensuring we use the UUID
            const formattedCompanies = companies.map(company => {
                // Log each company to help identify structure issues
                console.log("Processing company:", company);
                
                // Check if the company has the expected structure
                // CRITICAL: We need to use the actual UUID of the company, not a numeric ID
                const companyId = company.uuid || company.COMPANY_UUID || company.id || company.COMPANY_ID;
                const companyName = company.COMPANY_NAME || company.name || `Company ${companyId}`;
                
                return {
                    value: companyId, // Ensure we're using UUID here
                    label: companyName
                };
            })
            .filter(company => company.value && company.label); // Filter out any invalid entries
            
            console.log("Final formatted companies for dropdown:", formattedCompanies);
            
            if (formattedCompanies.length > 0) {
                setCompanyList(formattedCompanies);
            } else {
                console.warn("No valid companies after formatting, using fallback");
                // Fallback only if no valid companies were found after formatting
                const defaultCompanies = [
                    { value: "00000000-0000-0000-0000-000000000001", label: 'ACME Corporation' },
                    { value: "00000000-0000-0000-0000-000000000002", label: 'Example Company' },
                    { value: "00000000-0000-0000-0000-000000000003", label: 'Test Organization' }
                ];
                setCompanyList(defaultCompanies);
            }
        } else {
            console.warn("No companies found in state, using fallback");
            // Fallback for empty companies array - now using UUIDs
            const defaultCompanies = [
                { value: "00000000-0000-0000-0000-000000000001", label: 'ACME Corporation' },
                { value: "00000000-0000-0000-0000-000000000002", label: 'Example Company' },
                { value: "00000000-0000-0000-0000-000000000003", label: 'Test Organization' }
            ];
            setCompanyList(defaultCompanies);
        }
    }, [companies]);

    // Animation variants for staggered entry
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 12
            }
        }
    };

    // Function to get a fixed color based on name (for consistency)
    function getColorForName(name) {
        const colors = [
            'bg-pink-600',
            'bg-purple-600',
            'bg-yellow-500',
            'bg-green-500',
            'bg-blue-500',
            'bg-red-500',
            'bg-teal-500',
            'bg-indigo-600',
            'bg-orange-500',
        ];
        // Use a hash function to get a consistent color for each name
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        hash = Math.abs(hash);
        return colors[hash % colors.length];
    }

    function classNames(...classes) {
        return classes.filter(Boolean).join(' ');
    }

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const filteredUsers = users.filter(user => {
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
        const username = user.username ? user.username.toLowerCase() : '';
        const email = user.email ? user.email.toLowerCase() : '';
        const query = searchQuery.toLowerCase();
        
        return fullName.includes(query) || username.includes(query) || email.includes(query);
    });

    // Modify the validation function to check for email instead of username
    const hasValidationErrors = () => {
        // Basic validation errors - remove username check
        const missingRequiredFields = !userData.first_name || 
                                    !userData.last_name || !userData.email;
        
        // In edit mode, if email hasn't changed, don't check for duplicates
        if (isEditMode && currentEditingUser && 
            userData.email === currentEditingUser.email) {
            return missingRequiredFields;
        }
        
        // Return true if any validation error exists
        return missingRequiredFields || emailExists || usernameExists;
    };

    // Function to format the last login time in a minimalist way
    const formatLastLogin = (lastLogin) => {
        if (!lastLogin) return "Never logged in";
        
        try {
            const loginDate = new Date(lastLogin);
            // Check if date is valid
            if (isNaN(loginDate.getTime())) return "Invalid date";
            
            // Get the relative time (e.g., "3 days ago")
            const relativeTime = formatDistanceToNow(loginDate, { addSuffix: true });
            
            // For dates older than a week, just show the date without time
            const now = new Date();
            const diffDays = Math.floor((now - loginDate) / (1000 * 60 * 60 * 24));
            
            if (diffDays > 7) {
                // Format just MM/DD/YYYY for older dates
                return loginDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                });
            }
            
            // For recent dates, just show the relative time
            return relativeTime;
        } catch (error) {
            console.error("Error formatting date:", error);
            return "Date error";
        }
    };

    // Function to format dates consistently
    const formatDate = (dateString) => {
        if (!dateString) return "Unknown";
        
        try {
            const date = new Date(dateString);
            // Check if date is valid
            if (isNaN(date.getTime())) return "Invalid date";
            
            // Format as MM/DD/YYYY
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        } catch (error) {
            console.error("Error formatting date:", error);
            return "Date error";
        }
    };

    // Add this new function for exporting users to Excel
    const exportToExcel = () => {
        try {
            // Create a filtered list of users if search is active
            const dataToExport = searchQuery ? filteredUsers : users;
            
            if (!dataToExport || dataToExport.length === 0) {
                setSnackbarMessage('No users to export');
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
                return;
            }
            
            // Process user data to format it for export
            const exportData = dataToExport.map(user => {
                // Get user role information
                const userRoleInfo = userRoles[user.username] || {
                    isSuperuser: false,
                    djangoGroups: [],
                    auditRole: null
                };
                
                // Format role information
                const rolesList = [];
                if (userRoleInfo.isSuperuser) rolesList.push('Superuser');
                if (userRoleInfo.auditRole) rolesList.push(userRoleInfo.auditRole);
                if (userRoleInfo.djangoGroups && userRoleInfo.djangoGroups.length > 0) {
                    rolesList.push(...userRoleInfo.djangoGroups);
                }
                
                // Format dates
                const formattedJoinDate = user.date_joined ? format(new Date(user.date_joined), 'MM/dd/yyyy') : 'N/A';
                const formattedLastLogin = user.last_login ? format(new Date(user.last_login), 'MM/dd/yyyy HH:mm') : 'Never';
                const formattedDeactivationDate = user.deactivated_on ? format(new Date(user.deactivated_on), 'MM/dd/yyyy') : 'N/A';
                
                // Create formatted object for export
                return {
                    'User ID': user.id || 'N/A',
                    'Username': user.username || 'N/A',
                    'First Name': user.first_name || 'N/A',
                    'Last Name': user.last_name || 'N/A',
                    'Email': user.email || 'N/A',
                    'Status': user.is_active ? 'Active' : 'Inactive',
                    'Roles': rolesList.join(', ') || 'None',
                    'Is Superuser': userRoleInfo.isSuperuser ? 'Yes' : 'No',
                    'Date Joined': formattedJoinDate,
                    'Last Login': formattedLastLogin,
                    'Deactivation Date': user.is_active ? 'N/A' : formattedDeactivationDate,
                    'Deactivation Reason': (!user.is_active && user.deactivation_reason) ? user.deactivation_reason : 'N/A'
                };
            });
            
            // Create worksheet from data
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            
            // Adjust column widths for better readability
            const columnWidths = [
                { wch: 8 },  // User ID
                { wch: 20 }, // Username
                { wch: 15 }, // First Name
                { wch: 15 }, // Last Name
                { wch: 25 }, // Email
                { wch: 10 }, // Status
                { wch: 30 }, // Roles
                { wch: 12 }, // Is Superuser
                { wch: 12 }, // Date Joined
                { wch: 18 }, // Last Login
                { wch: 18 }, // Deactivation Date
                { wch: 30 }  // Deactivation Reason
            ];
            worksheet['!cols'] = columnWidths;
            
            // Create workbook and add the worksheet
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
            
            // Generate Excel file
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const fileData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
            
            // Get current date for filename
            const today = new Date();
            const dateString = format(today, 'yyyy-MM-dd');
            
            // Save file
            saveAs(fileData, `Users_Export_${dateString}.xlsx`);
            
            setSnackbarMessage('Users exported successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error exporting users:', error);
            setSnackbarMessage('Error exporting users: ' + error.message);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const customMainContent = (
        <div className="min-h-screen bg-gray-50">
            {/* Add a global style rule to hide "0" that shows up in select elements */}
            <style jsx="true">{`
                .MuiSelect-root + div {
                    display: none !important;
                }
                .MuiSelect-root input {
                    opacity: 0 !important;
                    height: 0 !important;
                    width: 0 !important;
                    position: absolute !important;
                }
            `}</style>
            
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
                            <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                            Home
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/Dashboard">
                            Dashboard
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/Dashboard">
                            System Settings
                        </mui.Link>
                        <mui.Typography color="text.primary">Users</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                        <UsersIcon className="h-7 w-7 mr-2 text-blue-500" aria-hidden="true" />
                        <mui.Box sx={{ flexGrow: 1 }}>
                            <mui.Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Manage Users
                            </mui.Typography>
                            <mui.Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Create, manage, and update users
                            </mui.Typography>
                        </mui.Box>
                        <mui.Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            onClick={exportToExcel}
                            sx={{
                                mr: 2,
                                borderRadius: '8px',
                                textTransform: 'none',
                                borderColor: 'rgba(25, 118, 210, 0.2)',
                                '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    borderColor: 'primary.main'
                                }
                            }}
                        >
                            Export to Excel
                        </mui.Button>
                        <mui.ButtonGroup size="small" sx={{ ml: 2 }}>
                            <mui.Button
                                variant={displayMode === 'grid' ? "contained" : "outlined"}
                                onClick={() => setDisplayMode('grid')}
                                startIcon={<mui.Icon>grid_view</mui.Icon>}
                                sx={{ textTransform: 'none' }}
                            >
                                Grid
                            </mui.Button>
                            <mui.Button
                                variant={displayMode === 'list' ? "contained" : "outlined"}
                                onClick={() => setDisplayMode('list')}
                                startIcon={<mui.Icon>view_list</mui.Icon>}
                                sx={{ textTransform: 'none' }}
                            >
                                List
                            </mui.Button>
                        </mui.ButtonGroup>
                    </mui.Box>

                    <mui.Box sx={{ mb: 3 }}>
                        <mui.TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search users by name, username or email..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <mui.InputAdornment position="start">
                                        <mui.Icon>search</mui.Icon>
                                    </mui.InputAdornment>
                                ),
                                sx: {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                    '& fieldset': {
                                        borderColor: 'rgba(0, 0, 0, 0.08)'
                                    }
                                }
                            }}
                            size="small"
                        />
                    </mui.Box>

                    <Separator />

                    <mui.Box sx={{ 
                        backgroundColor: '#f8fafc', 
                        borderRadius: 2,
                        minHeight: '70vh',
                        mt: 3,
                        boxShadow: 'inset 0 1px 2px 0 rgb(0 0 0 / 0.05)'
                    }}>
                        <mui.Paper 
                            elevation={0}
                            sx={{ 
                                p: 2,
                                height: '100%',
                                backgroundColor: 'transparent'
                            }}
                        >
                            {displayMode === 'grid' ? (
                                <motion.div 
                                    className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mt-2"
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                >
                                    {filteredUsers.map((user) => (
                                        <motion.div 
                                            key={user.username}
                                            variants={itemVariants}
                                            whileHover={{ 
                                                scale: 1.03, 
                                                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.05)" 
                                            }}
                                            transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                        >
                                            <mui.Paper
                                                component={mui.Link}
                                                onClick={() => handleButtonClick(user.username)}
                                                sx={{
                                                    p: 2,
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    borderRadius: 1.5,
                                                    backgroundColor: '#ffffff',
                                                    textDecoration: 'none',
                                                    cursor: 'pointer',
                                                    overflow: 'hidden',
                                                    transition: 'all 0.2s ease-in-out',
                                                    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.02)',
                                                    border: '1px solid',
                                                    borderColor: 'rgba(0, 0, 0, 0.06)',
                                                    '&:hover': {
                                                        backgroundColor: '#ffffff',
                                                        borderColor: 'rgba(25, 118, 210, 0.15)',
                                                        boxShadow: '0 3px 8px rgba(0,0,0,0.04)'
                                                    }
                                                }}
                                            >
                                                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                                    <DynamicAvatarList names={`${user.first_name || ''} ${user.last_name || user.username}`} />
                                                    <mui.Box sx={{ ml: 1.5 }}>
                                                        <mui.Typography 
                                                            variant="subtitle1" 
                                                            component="h3" 
                                                            sx={{ 
                                                                fontWeight: 600, 
                                                                color: 'text.primary',
                                                                fontSize: '0.9rem'
                                                            }}
                                                        >
                                                            {user.first_name && user.last_name 
                                                                ? `${user.first_name} ${user.last_name}` 
                                                                : user.username}
                                                        </mui.Typography>
                                                        <mui.Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                color: 'text.secondary',
                                                                fontSize: '0.75rem'
                                                            }}
                                                        >
                                                            {user.email || 'No email provided'}
                                                        </mui.Typography>
                                                        {renderRoleBadges(user.username)}
                                                    </mui.Box>
                                                </mui.Box>
                                                
                                                <mui.Chip 
                                                    label={user.is_active ? "Active" : "Inactive"} 
                                                    size="small"
                                                    sx={{ 
                                                        backgroundColor: user.is_active ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                                                        color: user.is_active ? '#1976d2' : '#606060',
                                                        alignSelf: 'flex-start',
                                                        mb: 1,
                                                        fontWeight: 500,
                                                        fontSize: '0.7rem',
                                                        height: '20px',
                                                        border: '1px solid',
                                                        borderColor: user.is_active ? 'rgba(25, 118, 210, 0.15)' : 'rgba(0, 0, 0, 0.1)'
                                                    }}
                                                />
                                                
                                                <mui.Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        color: 'text.secondary',
                                                        fontSize: '0.7rem',
                                                        mb: 1
                                                    }}
                                                >
                                                    <mui.Icon sx={{ fontSize: '0.9rem', mr: 0.5 }}>schedule</mui.Icon>
                                                    {formatLastLogin(user.last_login)}
                                                </mui.Typography>
                                                
                                                <mui.Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        color: 'text.secondary',
                                                        fontSize: '0.7rem',
                                                        mb: 1
                                                    }}
                                                >
                                                    <mui.Icon sx={{ fontSize: '0.9rem', mr: 0.5 }}>event</mui.Icon>
                                                    Created: {formatDate(user.date_joined)}
                                                </mui.Typography>
                                                
                                                {!user.is_active && (
                                                    <mui.Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            color: '#dc3545',
                                                            fontSize: '0.7rem',
                                                            mb: 1
                                                        }}
                                                    >
                                                        <mui.Icon sx={{ fontSize: '0.9rem', mr: 0.5 }}>do_not_disturb</mui.Icon>
                                                        Deactivated {user.deactivated_on ? `on ${formatDate(user.deactivated_on)}` : ''}
                                                    </mui.Typography>
                                                )}
                                                
                                                <mui.Box 
                                                    sx={{ 
                                                        mt: 'auto',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        color: 'primary.main',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    <mui.Box 
                                                        component="span" 
                                                        sx={{ 
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            transition: 'transform 0.2s ease-in-out',
                                                            transform: 'translateX(0)',
                                                            '.MuiPaper-root:hover &': {
                                                                transform: 'translateX(2px)'
                                                            }
                                                        }}
                                                    >
                                                        <OpenInNewIcon sx={{ fontSize: 14, ml: 0.3 }} />
                                                    </mui.Box>
                                                </mui.Box>
                                            </mui.Paper>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            ) : (
                                <motion.div
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    className="bg-white shadow-sm rounded-md overflow-hidden mt-2"
                                >
                                    <mui.List sx={{ padding: 0 }}>
                                        {filteredUsers.map((user) => (
                                            <motion.div
                                                key={user.username}
                                                variants={itemVariants}
                                            >
                                                <mui.ListItem
                                                    component={mui.Paper}
                                                    elevation={0}
                                                    onClick={() => handleButtonClick(user.username)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '10px 16px',
                                                        borderBottom: '1px solid',
                                                        borderColor: 'rgba(0, 0, 0, 0.06)',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: '#f9fafb'
                                                        },
                                                        transition: 'background-color 0.2s ease',
                                                        position: 'relative',
                                                        '&:hover::after': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            left: 0,
                                                            top: 0,
                                                            height: '100%',
                                                            width: '3px',
                                                            backgroundColor: 'primary.main',
                                                            opacity: 0.6
                                                        }
                                                    }}
                                                >
                                                    <DynamicAvatarList names={`${user.first_name || ''} ${user.last_name || user.username}`} />
                                                    
                                                    <mui.ListItemText
                                                        primary={
                                                            <mui.Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                                {user.first_name && user.last_name 
                                                                    ? `${user.first_name} ${user.last_name}` 
                                                                    : user.username}
                                                            </mui.Typography>
                                                        }
                                                        secondary={
                                                            <mui.Box>
                                                                <mui.Typography variant="body2" color="text.secondary">
                                                                    {user.email || 'No email provided'}
                                                                </mui.Typography>
                                                                {renderRoleBadges(user.username)}
                                                            </mui.Box>
                                                        }
                                                        sx={{ ml: 1.5 }}
                                                    />
                                                    
                                                    <mui.Chip 
                                                        label={user.is_active ? "Active" : "Inactive"} 
                                                        size="small"
                                                        sx={{ 
                                                            backgroundColor: user.is_active ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                                                            color: user.is_active ? '#1976d2' : '#606060',
                                                            mr: 2,
                                                            fontWeight: 500,
                                                            fontSize: '0.7rem',
                                                            height: '20px',
                                                            border: '1px solid',
                                                            borderColor: user.is_active ? 'rgba(25, 118, 210, 0.15)' : 'rgba(0, 0, 0, 0.1)'
                                                        }}
                                                    />
                                                    
                                                    <mui.Box sx={{ display: 'flex', flexDirection: 'column', mr: 2, minWidth: '150px' }}>
                                                        <mui.Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                color: 'text.secondary',
                                                                fontSize: '0.75rem',
                                                            }}
                                                        >
                                                            <mui.Icon sx={{ fontSize: '0.9rem', mr: 0.5 }}>schedule</mui.Icon>
                                                            {formatLastLogin(user.last_login)}
                                                        </mui.Typography>
                                                        
                                                        <mui.Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                color: 'text.secondary',
                                                                fontSize: '0.75rem',
                                                                mt: 0.5
                                                            }}
                                                        >
                                                            <mui.Icon sx={{ fontSize: '0.9rem', mr: 0.5 }}>event</mui.Icon>
                                                            Created: {formatDate(user.date_joined)}
                                                        </mui.Typography>
                                                        
                                                        {!user.is_active && (
                                                            <mui.Typography 
                                                                variant="caption" 
                                                                sx={{ 
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    color: '#dc3545',
                                                                    fontSize: '0.75rem',
                                                                    mt: 0.5
                                                                }}
                                                            >
                                                                <mui.Icon sx={{ fontSize: '0.9rem', mr: 0.5 }}>do_not_disturb</mui.Icon>
                                                                Deactivated {user.deactivated_on ? `on ${formatDate(user.deactivated_on)}` : ''}
                                                            </mui.Typography>
                                                        )}
                                                    </mui.Box>
                                                    
                                                    <mui.Button
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            borderRadius: '20px',
                                                            textTransform: 'none',
                                                            minWidth: '60px',
                                                            borderColor: 'rgba(0, 0, 0, 0.15)',
                                                            color: 'text.primary',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                                borderColor: 'rgba(0, 0, 0, 0.25)'
                                                            }
                                                        }}
                                                    >
                                                        View
                                                    </mui.Button>
                                                </mui.ListItem>
                                            </motion.div>
                                        ))}
                                    </mui.List>
                                </motion.div>
                            )}
                        </mui.Paper>
                    </mui.Box>
                </mui.Box>

                <CustomSpeedDial 
                    actions={[
                        {
                            icon: <AddIcon />,
                            name: 'Add User',
                            onClick: handleOpenCreateModal
                        }
                    ]}
                />

                <Modal
                    open={openCreateModal}
                    onClose={handleCloseCreateModal}
                    header={null}
                    body={
                        <mui.Box sx={{ 
                            p: { xs: 2, sm: 3 },
                            pt: { xs: 2, sm: 2 },
                            maxWidth: '550px',
                            margin: '0 auto',
                            position: 'relative'
                        }}>
                            <mui.Typography 
                                variant="h5" 
                                sx={{ 
                                    fontWeight: 600, 
                                    mb: 3, 
                                    color: 'text.primary',
                                    letterSpacing: '-0.01em',
                                    fontSize: '1.25rem',
                                    position: 'relative',
                                    '&:after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: '-8px',
                                        left: '0',
                                        width: '40px',
                                        height: '3px',
                                        backgroundColor: 'primary.main',
                                        borderRadius: '3px'
                                    }
                                }}
                            >
                                {isEditMode ? 'Edit User' : 'New User'}
                            </mui.Typography>
                            
                            <mui.Stack spacing={3}>
                                <mui.Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <mui.Box sx={{ flex: 1 }}>
                                        <mui.InputLabel 
                                            htmlFor="first_name" 
                                            sx={{ 
                                                fontSize: '0.75rem', 
                                                mb: 0.75, 
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            First Name*
                                        </mui.InputLabel>
                                        <mui.TextField
                                            id="first_name"
                                            name="first_name"
                                            value={userData.first_name}
                                            onChange={handleChange}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            placeholder="Enter first name"
                                            error={formSubmitted && !userData.first_name}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: '#f9fafb',
                                                    borderRadius: '8px',
                                                    transition: 'all 0.2s ease-in-out',
                                                    '& fieldset': {
                                                        borderColor: 'rgba(0,0,0,0.06)',
                                                        transition: 'all 0.2s',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'primary.light',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'primary.main',
                                                        borderWidth: '1px',
                                                    },
                                                    '&.Mui-focused': {
                                                        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)'
                                                    }
                                                }
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <mui.InputAdornment position="start">
                                                        <mui.Icon sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>badge</mui.Icon>
                                                    </mui.InputAdornment>
                                                ),
                                            }}
                                        />
                                    </mui.Box>
                                    <mui.Box sx={{ flex: 1 }}>
                                        <mui.InputLabel 
                                            htmlFor="last_name" 
                                            sx={{ 
                                                fontSize: '0.75rem', 
                                                mb: 0.75, 
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Last Name*
                                        </mui.InputLabel>
                                        <mui.TextField
                                            id="last_name"
                                            name="last_name"
                                            value={userData.last_name}
                                            onChange={handleChange}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            placeholder="Enter last name"
                                            error={formSubmitted && !userData.last_name}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: '#f9fafb',
                                                    borderRadius: '8px',
                                                    transition: 'all 0.2s ease-in-out',
                                                    '& fieldset': {
                                                        borderColor: 'rgba(0,0,0,0.06)',
                                                        transition: 'all 0.2s',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'primary.light',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'primary.main',
                                                        borderWidth: '1px',
                                                    },
                                                    '&.Mui-focused': {
                                                        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)'
                                                    }
                                                }
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <mui.InputAdornment position="start">
                                                        <mui.Icon sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>badge</mui.Icon>
                                                    </mui.InputAdornment>
                                                ),
                                            }}
                                        />
                                    </mui.Box>
                                </mui.Stack>
                                
                                <mui.Box>
                                    <mui.InputLabel 
                                        htmlFor="email" 
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            mb: 0.75, 
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        Email* (will be used as username)
                                    </mui.InputLabel>
                                    <mui.TextField
                                        id="email"
                                        name="email"
                                        value={userData.email}
                                        onChange={handleChange}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        placeholder="Enter email address"
                                        error={formSubmitted && !userData.email || emailExists || usernameExists}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#f9fafb',
                                                borderRadius: '8px',
                                                transition: 'all 0.2s ease-in-out',
                                                '& fieldset': {
                                                    borderColor: (emailExists || usernameExists) ? 'error.main' : 'rgba(0,0,0,0.06)',
                                                    transition: 'all 0.2s',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: (emailExists || usernameExists) ? 'error.main' : 'primary.light',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: (emailExists || usernameExists) ? 'error.main' : 'primary.main',
                                                    borderWidth: '1px',
                                                },
                                                '&.Mui-focused': {
                                                    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)'
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <mui.InputAdornment position="start">
                                                    <mui.Icon sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>email</mui.Icon>
                                                </mui.InputAdornment>
                                            ),
                                        }}
                                    />
                                    {emailExists && (
                                        <mui.Typography 
                                            variant="caption" 
                                            color="error" 
                                            sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                mt: 0.75, 
                                                fontSize: '0.7rem',
                                                gap: 0.5
                                            }}
                                        >
                                            <mui.Icon sx={{ fontSize: '0.9rem' }}>error_outline</mui.Icon>
                                            This email is already registered
                                        </mui.Typography>
                                    )}
                                    {usernameExists && !emailExists && (
                                        <mui.Typography 
                                            variant="caption" 
                                            color="error" 
                                            sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                mt: 0.75, 
                                                fontSize: '0.7rem',
                                                gap: 0.5
                                            }}
                                        >
                                            <mui.Icon sx={{ fontSize: '0.9rem' }}>error_outline</mui.Icon>
                                            This email is already used as a username
                                        </mui.Typography>
                                    )}
                                </mui.Box>
                                
                                <mui.Box>
                                    <mui.InputLabel 
                                        htmlFor="roles" 
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            mb: 0.75, 
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        Roles*
                                    </mui.InputLabel>
                                    <mui.FormControl 
                                        fullWidth 
                                        variant="outlined" 
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#f9fafb',
                                                borderRadius: '8px',
                                                transition: 'all 0.2s ease-in-out',
                                                '& fieldset': {
                                                    borderColor: 'rgba(0,0,0,0.06)',
                                                    transition: 'all 0.2s',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'primary.light',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'primary.main',
                                                    borderWidth: '1px',
                                                },
                                                '&.Mui-focused': {
                                                    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)'
                                                }
                                            }
                                        }}
                                    >
                                        <mui.Select
                                            id="roles"
                                            multiple
                                            value={selectedRoles.map(role => role.value)}
                                            onChange={handleRoleChange}
                                            displayEmpty
                                            renderValue={(selected) => {
                                                if (selected.length === 0) {
                                                    return <mui.Typography sx={{ color: 'text.secondary' }}>Select roles</mui.Typography>;
                                                }
                                                return (
                                                    <mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => {
                                                            const role = roleList.find(r => r.value === value);
                                                            return (
                                                                <mui.Chip 
                                                                    key={value} 
                                                                    label={role ? role.label : value}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: 'primary.lightest',
                                                                        color: 'primary.dark',
                                                                        fontWeight: 500,
                                                                        fontSize: '0.75rem',
                                                                        height: '24px',
                                                                        borderRadius: '4px',
                                                                        '& .MuiChip-deleteIcon': {
                                                                            color: 'primary.main',
                                                                            '&:hover': {
                                                                                color: 'primary.dark'
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </mui.Box>
                                                );
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 224,
                                                        width: 250,
                                                        borderRadius: '8px',
                                                        marginTop: '8px',
                                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                    },
                                                },
                                            }}
                                            startAdornment={
                                                <mui.InputAdornment position="start">
                                                    <mui.Icon sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>admin_panel_settings</mui.Icon>
                                                </mui.InputAdornment>
                                            }
                                        >
                                            {(roleList.length > 0 ? roleList : [
                                                { value: 1, label: 'Administrator' },
                                                { value: 2, label: 'Internal Auditor' },
                                                { value: 3, label: 'Process Owner' }
                                            ]).map((role) => (
                                                <mui.MenuItem key={role.value} value={role.value}>
                                                    <mui.Checkbox checked={selectedRoles.some(r => r.value === role.value)} />
                                                    <mui.ListItemText primary={role.label} />
                                                </mui.MenuItem>
                                            ))}
                                        </mui.Select>
                                    </mui.FormControl>
                                </mui.Box>
                                
                                <mui.Box>
                                    <mui.InputLabel 
                                        htmlFor="company" 
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            mb: 0.75, 
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        Company*
                                    </mui.InputLabel>
                                    <mui.FormControl 
                                        fullWidth 
                                        variant="outlined" 
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#f9fafb',
                                                borderRadius: '8px',
                                                transition: 'all 0.2s ease-in-out',
                                                '& fieldset': {
                                                    borderColor: 'rgba(0,0,0,0.06)',
                                                    transition: 'all 0.2s',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'primary.light',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'primary.main',
                                                    borderWidth: '1px',
                                                },
                                                '&.Mui-focused': {
                                                    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)'
                                                }
                                            }
                                        }}
                                    >
                                        <mui.Select
                                            id="company"
                                            multiple
                                            value={selectedCompany.map(company => company.value)}
                                            onChange={handleCompanyChange}
                                            displayEmpty
                                            renderValue={(selected) => {
                                                if (selected.length === 0) {
                                                    return <mui.Typography sx={{ color: 'text.secondary' }}>Select companies</mui.Typography>;
                                                }
                                                return (
                                                    <mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => {
                                                            const company = companyList.find(c => c.value === value);
                                                            return (
                                                                <mui.Chip 
                                                                    key={value} 
                                                                    label={company ? company.label : value}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: 'success.lightest',
                                                                        color: 'success.dark',
                                                                        fontWeight: 500,
                                                                        fontSize: '0.75rem',
                                                                        height: '24px',
                                                                        borderRadius: '4px',
                                                                        '& .MuiChip-deleteIcon': {
                                                                            color: 'success.main',
                                                                            '&:hover': {
                                                                                color: 'success.dark'
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </mui.Box>
                                                );
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 224,
                                                        width: 250,
                                                        borderRadius: '8px',
                                                        marginTop: '8px',
                                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                    },
                                                },
                                            }}
                                            startAdornment={
                                                <mui.InputAdornment position="start">
                                                    <mui.Icon sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>business</mui.Icon>
                                                </mui.InputAdornment>
                                            }
                                        >
                                            {companyList.map((company) => (
                                                <mui.MenuItem key={company.value} value={company.value}>
                                                    <mui.Checkbox checked={selectedCompany.some(c => c.value === company.value)} />
                                                    <mui.ListItemText primary={company.label} />
                                                </mui.MenuItem>
                                            ))}
                                        </mui.Select>
                                    </mui.FormControl>
                                </mui.Box>
                                
                                <mui.FormControlLabel
                                    control={
                                        <mui.Switch
                                            checked={userData.is_active}
                                            onChange={(e) => setNewUsers({...userData, is_active: e.target.checked})}
                                            color="primary"
                                            size="small"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#10b981',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(16, 185, 129, 0.08)',
                                                    },
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#10b981',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <mui.Icon sx={{ color: userData.is_active ? '#10b981' : 'text.secondary', mr: 0.5, fontSize: '1.2rem' }}>
                                                {userData.is_active ? 'check_circle' : 'cancel'}
                                            </mui.Icon>
                                            <mui.Typography variant="body2" sx={{ 
                                                color: userData.is_active ? '#10b981' : 'text.secondary', 
                                                fontWeight: 500 
                                            }}>
                                                {userData.is_active ? 'Account is active' : 'Account is inactive'}
                                            </mui.Typography>
                                        </mui.Box>
                                    }
                                    sx={{ 
                                        ml: 0, 
                                        mt: 1,
                                        border: '1px solid',
                                        borderColor: 'rgba(0,0,0,0.08)',
                                        borderRadius: '8px',
                                        p: 1.5,
                                        backgroundColor: '#f9fafb',
                                    }}
                                />

                                {/* Display reason field when deactivating */}
                                {!userData.is_active && (
                                    <mui.Box sx={{ mt: 2 }}>
                                        <mui.InputLabel 
                                            htmlFor="deactivation_reason" 
                                            sx={{ 
                                                fontSize: '0.75rem', 
                                                mb: 0.75, 
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Deactivation Reason
                                        </mui.InputLabel>
                                        <mui.TextField
                                            id="deactivation_reason"
                                            name="deactivation_reason"
                                            value={userData.deactivation_reason}
                                            onChange={handleChange}
                                            placeholder="Why is this user being deactivated?"
                                            fullWidth
                                            multiline
                                            rows={2}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: '#f9fafb',
                                                    borderRadius: '8px',
                                                    transition: 'all 0.2s ease-in-out',
                                                    '& fieldset': {
                                                        borderColor: 'rgba(0,0,0,0.06)',
                                                        transition: 'all 0.2s',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'primary.light',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'primary.main',
                                                        borderWidth: '1px',
                                                    },
                                                    '&.Mui-focused': {
                                                        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)'
                                                    }
                                                }
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <mui.InputAdornment position="start">
                                                        <mui.Icon sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>note</mui.Icon>
                                                    </mui.InputAdornment>
                                                ),
                                            }}
                                        />
                                    </mui.Box>
                                )}
                            </mui.Stack>
                        </mui.Box>
                    }
                    footer={
                        <mui.Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            p: 3, 
                            pt: 2,
                            borderTop: '1px solid',
                            borderColor: 'rgba(0,0,0,0.05)',
                            mt: 2,
                            backgroundColor: '#f9fafb',
                            borderBottomLeftRadius: '8px',
                            borderBottomRightRadius: '8px',
                        }}>
                            <mui.Stack direction="row" spacing={2}>
                                <mui.Button 
                                    onClick={handleCloseCreateModal}
                                    variant="outlined"
                                    startIcon={<mui.Icon>close</mui.Icon>}
                                    sx={{ 
                                        color: 'text.secondary',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        borderColor: 'rgba(0,0,0,0.1)',
                                        borderRadius: '8px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.02)',
                                            borderColor: 'rgba(0,0,0,0.2)'
                                        }
                                    }}
                                >
                                    Cancel
                                </mui.Button>
                                <mui.Button 
                                    onClick={createOrUpdateUser} 
                                    variant="contained" 
                                    disableElevation
                                    disabled={hasValidationErrors()}
                                    startIcon={<mui.Icon>{isEditMode ? 'update' : 'add_circle'}</mui.Icon>}
                                    sx={{
                                        textTransform: 'none',
                                        px: 3,
                                        py: 0.75,
                                        borderRadius: '8px',
                                        fontWeight: 500,
                                        backgroundColor: isEditMode ? '#10b981' : '#1976d2',
                                        boxShadow: isEditMode 
                                            ? '0 4px 6px rgba(16, 185, 129, 0.12), 0 2px 4px rgba(16, 185, 129, 0.08)'
                                            : '0 4px 6px rgba(25, 118, 210, 0.12), 0 2px 4px rgba(25, 118, 210, 0.08)',
                                        '&:hover': {
                                            backgroundColor: isEditMode ? '#059669' : '#1565c0',
                                            boxShadow: isEditMode
                                                ? '0 6px 10px rgba(5, 150, 105, 0.15), 0 3px 6px rgba(5, 150, 105, 0.1)'
                                                : '0 6px 10px rgba(21, 101, 192, 0.15), 0 3px 6px rgba(21, 101, 192, 0.1)',
                                        },
                                        fontSize: '0.875rem',
                                        transition: 'all 0.2s ease-in-out',
                                        '&.Mui-disabled': {
                                            backgroundColor: isEditMode
                                                ? 'rgba(16, 185, 129, 0.3)'
                                                : 'rgba(25, 118, 210, 0.3)',
                                            color: 'white'
                                        }
                                    }}
                                >
                                    {isEditMode ? 'Update User' : 'Create User'}
                                </mui.Button>
                            </mui.Stack>
                        </mui.Box>
                    }
                />

                <DynamicSnackbar
                    open={snackbarOpen}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                    onClose={handleSnackbarClose}
                />
            </ResponsiveContainer>
        </div>
    );

    return (
        <div>
            <SideBar mainContent={customMainContent} />
        </div>
    );
}

export default ManageUsers;
